import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api';
import { applyTheme, resetTheme } from './theme';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('assoperf_user') || 'null'); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  // Apply theme when user changes
  useEffect(() => {
    if (user?.couleur_principale && user.role !== 'super_admin') {
      applyTheme(user.couleur_principale);
    } else {
      resetTheme();
    }
  }, [user]);

  const login = useCallback(async (email, mot_de_passe) => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, mot_de_passe });
      localStorage.setItem('assoperf_token', data.token);
      localStorage.setItem('assoperf_refresh', data.refresh_token);
      localStorage.setItem('assoperf_user', JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  }, []);

  const demoLogin = useCallback(async (demo_key) => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/demo-login', { demo_key });
      localStorage.setItem('assoperf_token', data.token);
      localStorage.setItem('assoperf_refresh', data.refresh_token);
      localStorage.setItem('assoperf_user', JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  }, []);

  const register = useCallback(async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register', payload);
      localStorage.setItem('assoperf_token', data.token);
      localStorage.setItem('assoperf_refresh', data.refresh_token);
      // After register we need full user → call /me
      const me = await api.get('/auth/me');
      const userData = { ...me.data, couleur_principale: payload.asso_couleur, asso_nom: payload.asso_nom };
      localStorage.setItem('assoperf_user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } finally { setLoading(false); }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('assoperf_token');
    localStorage.removeItem('assoperf_refresh');
    localStorage.removeItem('assoperf_user');
    setUser(null);
    // Clear permissions cache to avoid stale state on next login
    try { require('./usePermissions').clearPermissionsCache(); } catch {}
    resetTheme();
    window.location.href = '/login';
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      const merged = { ...(user || {}), ...data };
      localStorage.setItem('assoperf_user', JSON.stringify(merged));
      setUser(merged);
    } catch {}
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, demoLogin, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
